import { Dropdown, IconButton } from '@dixa/lib-shared-habla';
import { ConversationsChannels } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import { useRef, useState } from 'react';
import { useClickAway } from 'react-use';

import { AwayStatus } from 'apis/presence';
import { getChannelName } from 'utils/channels';
import { mediaAttributes, MetaWorkingMedia } from 'utils/channels/media';
import { listToAPStyleComma } from 'utils/formatting/string';
import {
  requestMicrophoneAccess,
  RequestMicrophoneAccessError,
} from 'utils/telephony/microphone';

import {
  getSimulaneousRingHelpText,
  getSimulaneousRingHelpTitle,
} from '../utils/simultaneousRingHelpText';

import {
  PresenceDropdownItem,
  PresenceDropdownSeparator,
  PresenceToggle,
} from './PresenceDropdownItem';
import { PresenceStatusItem } from './PresenceStatusItem';
import styles from './presenceDropdown.module.scss';

const channelLabelMap = {
  voicemail: 'Voicemail',
  email: 'Email',
  contactform: 'Contact form',
  callback: 'Callback',
  pstnphone: 'Phone',
  interactivechat: 'Chat',
  speak: 'Voice',
  messaging: 'Messaging',
};

type Props = {
  metaWorkingMedia: MetaWorkingMedia[];
  hasAccess: boolean;
  hasPhoneChannel: boolean;
  errorMicrophoneErrorShowModal: (payload?: any) => void;
  showLogoutModal: () => void;
  presence: any;
  oppositePresenceStatus: string;
  telephony: any;
  presenceSetMedia: any;
  presenceSetMediaChannel: any;
  setCustomAwayStatus: (
    awayStatus: Pick<AwayStatus, 'id' | 'label' | 'emoji'>,
  ) => void;
  isPresenceStatusLoading: boolean;
  personalToggleRememberMicrophoneAccess: any;
  validPhoneNumber: any;
  isInTrial: boolean;
  profile: any;
  toggleSimultaneousRing: (location: string) => void;
  changeYourStatus: (location: 'topbar' | 'topbarDropdown') => void;
};

export const PresenceDropdown = ({
  metaWorkingMedia,
  errorMicrophoneErrorShowModal,
  presence,
  hasAccess,
  hasPhoneChannel,
  oppositePresenceStatus,
  telephony,
  presenceSetMedia,
  presenceSetMediaChannel,
  setCustomAwayStatus,
  personalToggleRememberMicrophoneAccess,
  validPhoneNumber,
  isInTrial,
  isPresenceStatusLoading,
  profile,
  toggleSimultaneousRing,
  changeYourStatus,
  showLogoutModal,
}: Props): JSX.Element => {
  const [showDropdown, setShowDropdown] = useState(false);
  const { custom_away_status_id } = presence?.presence_status;

  const dropdownRef = useRef(null);
  useClickAway(dropdownRef, () => {
    setShowDropdown(false);
  });

  const handleMediaClick = (metaMedia: MetaWorkingMedia): void => {
    if (metaMedia.name === 'speak' && telephony.isMicrophoneBlocked) {
      requestMicrophoneAccess()
        .then(errorMicrophoneErrorShowModal)
        .catch(errorMicrophoneErrorShowModal)
        .finally(() => {
          // Remove the remember my microphone access.
          personalToggleRememberMicrophoneAccess({
            value: false,
          });
        });
    } else {
      const working = !metaMedia.working;
      presenceSetMedia({ media: metaMedia.name, working }).then(() => {
        ampli.presenceMediaUpdated({
          media: metaMedia.name,
          enabled: working,
          location: 'topbarDropdown',
        });
      });
    }
  };

  const handleChannelClick = (
    conversation_channel: ConversationsChannels,
  ): void => {
    const workingMedia = presence.working_channels.find(
      ({ conversation_channel: workingChannel }) =>
        workingChannel === conversation_channel,
    );
    if (
      conversation_channel === 'pstnphone' ||
      conversation_channel === 'callback'
    ) {
      if (telephony.isMicrophoneBlocked === true) {
        requestMicrophoneAccess()
          .then(() => {
            errorMicrophoneErrorShowModal();
          })
          .catch((microphoneResponse: RequestMicrophoneAccessError) => {
            errorMicrophoneErrorShowModal(microphoneResponse);
          });
      } else {
        presenceSetMediaChannel({
          working: !workingMedia.working,
          channel: conversation_channel,
        });
      }
    } else {
      presenceSetMediaChannel({
        working: !workingMedia.working,
        channel: conversation_channel,
      });
    }
  };

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <IconButton
        className={styles.dropdownButton}
        icon="angle-down"
        variant="secondary"
        onClick={() => {
          setShowDropdown(!showDropdown);
        }}
        data-testid="presence-dropdown-button"
      ></IconButton>
      <Dropdown isOpen={showDropdown} data-testid="presence-dropdown-menu">
        <ul className={styles.dropdown}>
          <PresenceStatusItem
            awayStatusId={custom_away_status_id}
            isStatusLoading={isPresenceStatusLoading}
            status={presence.status}
            oppositeStatus={oppositePresenceStatus}
            changeOwnStatus={() => changeYourStatus('topbarDropdown')}
            setCustomAwayStatus={setCustomAwayStatus}
            showPresenceDropdown={() => setShowDropdown(true)}
          />
          <PresenceDropdownSeparator />
          {metaWorkingMedia.map((metaMedia) => {
            const tooltip = listToAPStyleComma(
              metaMedia.channels.map(getChannelName),
            );
            const mediaName = mediaAttributes[metaMedia.name].name;
            const tooltipButton = hasAccess
              ? `Turn ${mediaName} ${metaMedia.working ? 'off' : 'on'}`
              : "You don't have access to enable media channels";
            return (
              <PresenceDropdownItem
                key={metaMedia.name}
                icon={mediaAttributes[metaMedia.name].icon}
                text={channelLabelMap[metaMedia.name]}
                tooltipText={tooltip}
                subitems={
                  metaMedia.name === 'speak'
                    ? [
                        <PresenceDropdownItem
                          key={'speak'}
                          icon="voicemail"
                          text="Voicemail"
                          isSubitem
                        >
                          <PresenceToggle
                            id="voicemail"
                            active={
                              presence.working_channels.find(
                                ({ conversation_channel }) =>
                                  conversation_channel === 'voicemail',
                              )?.working
                            }
                            disabled={!hasAccess}
                            isAway={presence.status !== 'working'}
                            onToggle={() => {
                              handleChannelClick('voicemail');
                            }}
                          />
                        </PresenceDropdownItem>,
                      ]
                    : undefined
                }
              >
                <PresenceToggle
                  id={metaMedia.name}
                  active={metaMedia.working}
                  isAway={presence.status !== 'working'}
                  disabled={!hasAccess}
                  onToggle={() => {
                    handleMediaClick(metaMedia);
                  }}
                  tooltipText={tooltipButton}
                />
              </PresenceDropdownItem>
            );
          })}
          <PresenceDropdownSeparator />
          {hasPhoneChannel && (
            <>
              <PresenceDropdownItem
                icon="simultaneous"
                text="Simultaneous Ring"
                helpText={getSimulaneousRingHelpText(
                  validPhoneNumber,
                  isInTrial,
                  profile.phone_number,
                )}
                helpTitle={getSimulaneousRingHelpTitle(
                  validPhoneNumber,
                  isInTrial,
                )}
              >
                <PresenceToggle
                  id="simultaneous-ring"
                  active={
                    presence.presence_status.simultaneous_ringing === true
                  }
                  disabled={!hasAccess}
                  onToggle={() => toggleSimultaneousRing('topbarDropdown')}
                  isAway={presence.status !== 'working'}
                  tooltipText={
                    !hasAccess
                      ? "You don't have access to enable simultaneous ring"
                      : ''
                  }
                />
              </PresenceDropdownItem>
              <PresenceDropdownSeparator />
            </>
          )}

          <PresenceDropdownItem
            icon="sign-out"
            text="Sign out"
            onClick={showLogoutModal}
          />
        </ul>
      </Dropdown>
    </div>
  );
};
